import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Printer, Trash2, Eye, Wand2, BarChart3, Calendar, FileDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { fetchMyPlans, deletePlan } from "@/lib/api";
import { MD } from "@/components/MD";
import ClassesPanel from "@/components/ClassesPanel";
import PlanComments from "@/components/PlanComments";
import WatchlistWidget from "@/components/WatchlistWidget";
import { exportLessonPlanPdf } from "@/lib/pdf";

export default function TeacherDashboard() {
    const { t } = useLang();
    const { user, isTeacher, loading } = useAuth();
    const nav = useNavigate();
    const [plans, setPlans] = useState([]);
    const [selected, setSelected] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            nav("/login", { replace: true });
            return;
        }
        if (!isTeacher) {
            nav("/", { replace: true });
            return;
        }
        fetchMyPlans()
            .then((p) => {
                setPlans(p);
                if (p.length) setSelected(p[0]);
            })
            .finally(() => setPageLoading(false));
    }, [user, isTeacher, loading, nav]);

    const remove = async (id) => {
        if (!window.confirm(t.dashboard.confirm)) return;
        await deletePlan(id);
        const next = plans.filter((p) => p.id !== id);
        setPlans(next);
        setSelected(next[0] || null);
    };

    const printPlan = () => {
        if (!selected) return;
        document.body.classList.add("printing-plan");
        window.print();
        setTimeout(() => document.body.classList.remove("printing-plan"), 200);
    };

    return (
        <main
            className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-24 relative"
            data-testid="dashboard-page"
        >
            <div className="glow glow-coral -top-20 right-10" />
            <div className="relative z-10">
                <div className="flex items-end justify-between flex-wrap gap-4">
                    <div>
                        <span className="tag">// teacher panel</span>
                        <h1 className="font-display font-black text-white text-4xl md:text-6xl mt-3 uppercase leading-[0.95]">
                            {t.dashboard.head}
                        </h1>
                        {user && (
                            <p className="font-mono text-sm text-[var(--muted)] mt-2">
                                {user.name} · {user.email}
                            </p>
                        )}
                    </div>
                    <Link to="/teach" className="btn-prime" data-testid="dash-new-plan">
                        <Wand2 size={14} /> {t.dashboard.generate}
                    </Link>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
                    <StatCard
                        Icon={BarChart3}
                        label={t.dashboard.totalPlans}
                        value={plans.length}
                        color="var(--lime)"
                    />
                    <StatCard
                        Icon={Calendar}
                        label={t.dashboard.recent}
                        value={plans[0]?.topic || "—"}
                        color="var(--cyan)"
                        small
                    />
                    <StatCard
                        Icon={Wand2}
                        label="Auth method"
                        value={user?.auth_method || "—"}
                        color="var(--coral)"
                    />
                </div>

                {/* Tank Health Watchlist — flags up classes with active water issues */}
                <WatchlistWidget />

                {/* Classes section above lesson plans so it's always reachable */}
                <ClassesPanel />

                {/* Lesson plans heading */}
                <h2 className="font-display font-bold text-[var(--lime)] uppercase text-2xl tracking-widest mt-14 mb-5">
                    {t.dashboard.myPlans}
                </h2>

                {/* Plans list + viewer */}
                {pageLoading ? (
                    <p className="font-mono text-[var(--muted)] mt-12">
                        {t.common.loading}
                    </p>
                ) : plans.length === 0 ? (
                    <div className="card p-10 mt-10 text-center">
                        <p className="font-brush text-[var(--lime)] text-4xl -rotate-3 inline-block">
                            no plans yet
                        </p>
                        <p className="font-mono text-[var(--muted)] mt-3">
                            {t.dashboard.none}
                        </p>
                        <Link
                            to="/teach"
                            className="btn-coral mt-6 inline-flex"
                            data-testid="dash-empty-cta"
                        >
                            {t.dashboard.generate}
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-12 gap-6 mt-10">
                        <aside className="col-span-12 md:col-span-4 space-y-3" data-testid="dash-plans-list">
                            {plans.map((p) => (
                                <div
                                    key={p.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelected(p)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            setSelected(p);
                                        }
                                    }}
                                    data-testid={`plan-item-${p.id}`}
                                    className={
                                        "w-full text-left card p-4 cursor-pointer transition-colors " +
                                        (selected?.id === p.id
                                            ? "border-[var(--lime)] bg-[var(--lime)]/5"
                                            : "")
                                    }
                                >
                                    <div className="font-display text-white text-base leading-snug">
                                        {p.topic}
                                    </div>
                                    <div className="font-mono text-xs text-[var(--muted)] mt-1">
                                        {p.grade_level} · {p.duration_min} min ·{" "}
                                        {new Date(p.created_at).toLocaleDateString()}
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <span
                                            className="font-mono text-[10px] uppercase tracking-widest text-[var(--cyan)]"
                                        >
                                            <Eye size={12} className="inline mr-1" />
                                            {t.dashboard.view}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                remove(p.id);
                                            }}
                                            data-testid={`plan-delete-${p.id}`}
                                            className="font-mono text-[10px] uppercase tracking-widest text-[var(--coral)] ml-auto"
                                        >
                                            <Trash2 size={12} className="inline mr-1" />
                                            {t.dashboard.del}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </aside>
                        <section className="col-span-12 md:col-span-8" data-testid="dash-plan-view">
                            {selected && (
                                <div id="print-target" className="card p-6 md:p-8">
                                    <div className="flex items-center justify-between mb-4 no-print">
                                        <span className="tag">// {selected.topic}</span>
                                        <div className="flex items-center gap-4">
                                            <button
                                                onClick={() => exportLessonPlanPdf(selected)}
                                                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--coral)] hover:text-[var(--lime)] inline-flex items-center gap-2"
                                                data-testid="dash-plan-pdf"
                                            >
                                                <FileDown size={14} /> PDF
                                            </button>
                                            <button
                                                onClick={printPlan}
                                                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--cyan)] hover:text-[var(--lime)] inline-flex items-center gap-2"
                                                data-testid="plan-print"
                                            >
                                                <Printer size={14} /> {t.dashboard.print}
                                            </button>
                                        </div>
                                    </div>
                                    <MD text={selected.plan_markdown} />
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </div>
        </main>
    );
}

function StatCard({ Icon, label, value, color, small }) {
    return (
        <div className="card p-5">
            <Icon size={18} style={{ color }} />
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mt-2">
                {label}
            </div>
            <div
                className={
                    (small
                        ? "font-mono text-base mt-1"
                        : "font-display font-black text-3xl mt-1") +
                    " text-white truncate"
                }
            >
                {value}
            </div>
        </div>
    );
}
